/** @jsx jsx */

import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { css, jsx, keyframes } from '@emotion/core';
import { ChromePicker } from 'react-color';
import useSetState from 'hooks/useSetState';
import Dot from './Dot';
import UserPalette from './UserPalette';
import EditorBtn from 'components/UI/EditorBtn';
import { useEffect } from 'react';

const fadeInColorPicker = keyframes`
0% {
  opacity: 0;
}

25% {
  opacity: 0;
}

100% {
  opacity: 1;
}
`;

let container;
let hideRGBAccess;

interface IFixedColorPicker {
  inColor?: any;
  outColor?: any;
  clearedColor?: string;
  style?: any;
}

const ColorPicker = ({
  inColor,
  outColor,
  clearedColor = '',
  style = {},
}: IFixedColorPicker) => {
  const { state, setState } = useSetState({
    open: false,
    color: inColor,
    pickerLocation: [0, 0],
  } as any);

  const { open, color, pickerLocation } = state;

  const isChanging = useRef<Boolean>(false);
  const didChange = useRef<Boolean>(false);
  const pickerContainer = useRef<HTMLDivElement>(null);
  const chromePickerContainer = useRef<HTMLDivElement>(null);
  const pickerBounds = useRef<DOMRect | undefined>();

  if (!container) {
    container = document.querySelector('#color-picker-container');
  }

  useEffect(() => {
    if (!open) {
      return;
    }

    const pickerContainerBounds = pickerContainer.current!.getBoundingClientRect();

    const offsetX = 40;
    const offsetY = 20;

    const { left, top } = pickerContainerBounds;
    const overlapX = left + pickerBounds.current!.width - window.innerWidth;
    const overlapY = top + pickerBounds.current!.height - window.innerHeight;

    setState({
      pickerLocation: [
        overlapX > 0 ? left - (overlapX + offsetX) : left + offsetX,
        overlapY > 0 ? top - (overlapY + offsetY) : top - offsetY,
      ],
    });
  }, [open]);

  const onOpen = () => {
    didChange.current = false;

    // dirty trick to hide the rgbAccess etc
    hideRGBAccess = setInterval(() => {
      if (!chromePickerContainer.current) return;
      Array.from(
        chromePickerContainer.current!.querySelectorAll('div')
      ).forEach((_: any) => {
        const $ = _.querySelector('svg');
        if (_.querySelector('svg') && _.style.width === '32px') {
          _.style.display = 'none';
        }

        //
        Array.from(_.querySelectorAll('span')).forEach((span: any) => {
          if (span.innerText.toLowerCase() === 'hex') {
            span.style.display = 'none';
          }
        });

        if (_.style.display === 'none') clearInterval(hideRGBAccess);
      }, 0);
    });

    closeEvents('add');

    setState({
      open: true,
    });
  };

  const onChange = result => {
    if (result.hex === color) return (didChange.current = false);

    isChanging.current = true;
    if (!didChange.current) didChange.current = true;

    setState({
      color: result.hex,
    });
  };

  const forceClose = () => {
    closeEvents('remove');

    setState({
      open: false,
    });
  };

  const closeEvents = dir => {
    window[`${dir}EventListener`]('scroll', forceClose);
    window[`${dir}EventListener`]('resize', forceClose);
  };

  const parsedColor = !isChanging.current ? inColor : color;
  if (!open) {
    isChanging.current = false;
  }

  if (!container) {
    return null;
  }

  return (
    <div ref={pickerContainer}>
      <Dot color={parsedColor} onOpen={onOpen} style={style} />
      {open &&
        createPortal(
          <div
            css={css`
              position: fixed;
              left: 0px;
              top: 0px;
              width: 100vw;
              height: 100vh;
              z-index: 1;
              //
              visibility: ${pickerLocation.every(entry => entry)
                ? 'visible'
                : 'hidden'};
            `}
            onClick={() => {
              isChanging.current = false;
              didChange.current && outColor(color);
              requestAnimationFrame(() => {
                setState({ open: false });
              });
            }}
          >
            <div
              ref={node => {
                if (node) {
                  pickerBounds.current = node.getBoundingClientRect();
                }
              }}
              css={css`
                position: absolute;
                left: ${pickerLocation[0]}px;
                top: ${pickerLocation[1]}px;
                padding: 10px;
                border-radius: 5px;
                background: #fff;
                box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
                * {
                  font-family: 'Poppins';
                }
                .chrome-picker {
                  box-shadow: none !important;
                }
                animation: ${fadeInColorPicker} 0.3s;
              `}
              onMouseUp={() => {
                isChanging.current = false;
                didChange.current && outColor(color);
              }}
              onKeyUp={e => {
                isChanging.current = false;
                e.target['type'] === 'text' && outColor(color);
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                ref={chromePickerContainer}
                css={css`
                  display: flex;
                  justify-content: center;
                  width: 100%;
                `}
              >
                {/* @ts-ignore */}
                <ChromePicker
                  color={parsedColor}
                  onChange={onChange}
                  disableAlpha
                />
              </div>

              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  margin-bottom: 10px;
                `}
              >
                <EditorBtn
                  label="Clear colour"
                  style={{
                    minHeight: 'auto',
                    height: '20px !important',
                    padding: '5px 30px',
                    fontSize: 14,
                  }}
                  onClick={e => {
                    onChange({
                      hex: clearedColor !== undefined ? clearedColor : '',
                    });
                    requestAnimationFrame(() => outColor(clearedColor));
                  }}
                />
              </div>

              {/* @ts-ignore */}
              <UserPalette
                key={color}
                currentColor={color}
                selectColor={color => {
                  onChange({ hex: color });
                  requestAnimationFrame(() => outColor(color));
                }}
              />
            </div>
          </div>,
          container
        )}
    </div>
  );
};

export default ColorPicker;
